import React, { useState, useEffect } from 'react';
import styles from './VideoReels.module.css';
import withEditRights from '../hoc/with-edit-rights';
import apiClient from '../../util/api-client';
import { getVideos as getLocalVideos, deleteVideo as deleteLocalVideo } from '../../util/video-storage';

const getEmbedInfo = (url) => {
  if (!url) return { isEmbed: false, src: '' };
  // YouTube URLs
  if (url.includes('youtube.com/watch') || url.includes('youtu.be/')) {
    let videoId = '';
    if (url.includes('v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    if (videoId) {
      return { isEmbed: true, src: `https://www.youtube.com/embed/${videoId}` };
    }
  }
  // Vimeo URLs
  if (url.includes('vimeo.com/')) {
    const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
    if (videoId) {
      return { isEmbed: true, src: `https://player.vimeo.com/video/${videoId}` };
    }
  }
  return { isEmbed: false, src: url };
};

const fileToDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const VideoReels = ({ canEdit }) => {
  const [videos, setVideos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');

  // Ladda videor från molndatabasen (MongoDB) och synka lokala videor
  useEffect(() => {
    let isMounted = true;

    const loadAllVideos = async () => {
      try {
        const cloudVideos = await apiClient.getVideos();
        if (isMounted && Array.isArray(cloudVideos)) {
          setVideos(cloudVideos);
        }

        // Automatisk migrering: Om det finns lokala videor i IndexedDB, synka till MongoDB
        try {
          const localVideos = await getLocalVideos();
          if (localVideos && localVideos.length > 0) {
            for (const localV of localVideos) {
              const alreadyInCloud = cloudVideos && cloudVideos.some(
                (cv) => cv.filename === localV.filename || cv.title === localV.title
              );
              if (!alreadyInCloud && localV.blob) {
                if (localV.blob.size <= 4.5 * 1024 * 1024) {
                  const dataUrl = await fileToDataUrl(localV.blob);
                  const uploaded = await apiClient.saveVideo({
                    title: localV.title || localV.filename,
                    url: dataUrl,
                    filename: localV.filename,
                    type: 'file',
                  });
                  if (uploaded && isMounted) {
                    setVideos((prev) => [uploaded, ...prev]);
                  }
                }
              }
            }
          }
        } catch (localErr) {
          console.warn('Lokal videosynk-notis:', localErr.message);
        }
      } catch (error) {
        console.error('Fel vid hämtning av videor:', error);
      }
    };

    loadAllVideos();

    return () => {
      isMounted = false;
    };
  }, []);

  // Hantera filuppladdning till MongoDB Atlas
  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (!files || files.length === 0) return;

    setIsUploading(true);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadStatus(`Laddar upp "${file.name}" (${i + 1}/${files.length})...`);

      if (file.size > 4.5 * 1024 * 1024) {
        alert(
          `Videon "${file.name}" är ${(file.size / 1024 / 1024).toFixed(1)} MB.\n\n` +
          `Maxgräns för direktfil till molnet är 4.5 MB. För längre videor eller stora filer: använd knappen "Klistra in videolänk" (t.ex. YouTube, Google Drive eller Vimeo)!`
        );
        continue;
      }

      try {
        const dataUrl = await fileToDataUrl(file);
        const savedVideo = await apiClient.saveVideo({
          title: file.name.replace(/\.[^/.]+$/, ''),
          url: dataUrl,
          filename: file.name,
          type: 'file',
        });

        if (savedVideo) {
          setVideos((prev) => [savedVideo, ...prev]);
        }
      } catch (error) {
        console.error('Kunde inte spara video till molnet:', error);
        alert(`Kunde inte spara "${file.name}". Kontrollera filen och försök igen.`);
      }
    }

    setIsUploading(false);
    setUploadStatus('');
    event.target.value = '';
  };

  // Hantera videolänk (YouTube, Vimeo, MP4-URL)
  const handleAddLinkSubmit = async (e) => {
    e.preventDefault();
    if (!linkUrl.trim()) return;

    setIsUploading(true);
    setUploadStatus('Sparar videolänk...');

    try {
      const embedCheck = getEmbedInfo(linkUrl.trim());
      const savedVideo = await apiClient.saveVideo({
        title: linkTitle.trim() || 'Video Reel',
        url: linkUrl.trim(),
        filename: 'link',
        type: embedCheck.isEmbed ? 'embed' : 'file',
      });

      if (savedVideo) {
        setVideos((prev) => [savedVideo, ...prev]);
      }

      setLinkUrl('');
      setLinkTitle('');
      setShowLinkModal(false);
    } catch (err) {
      console.error('Kunde inte spara videolänk:', err);
      alert('Kunde inte spara videolänken.');
    } finally {
      setIsUploading(false);
      setUploadStatus('');
    }
  };

  // Hantera videoborttagning från MongoDB och lokal cache
  const handleRemoveVideo = async (videoItem, index) => {
    const videoId = videoItem.id || videoItem._id || videoItem.filename;
    const confirmDelete = window.confirm(`Är du säker på att du vill ta bort "${videoItem.title || 'videon'}"?`);
    if (!confirmDelete) return;

    try {
      await apiClient.deleteVideo(videoId);
      if (videoItem.id && typeof videoItem.id === 'number') {
        try {
          await deleteLocalVideo(videoItem.id);
        } catch (e) {}
      }

      setVideos((prev) => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Kunde inte ta bort video:', error);
      alert('Kunde inte radera videon från servern.');
    }
  };

  return (
    <div className={styles.videoReelsContainer}>
      <h2 className={styles.title}>Ladda upp Reels och Videor</h2>

      {/* Knappar och funktioner för lärare / admin */}
      {canEdit && (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '32px' }}>
          <label htmlFor="videoUpload" className={styles.uploadLabel} style={{ marginBottom: 0 }}>
            {isUploading ? 'Laddar upp...' : 'Välj Videor (Fil)'}
          </label>
          <input
            type="file"
            id="videoUpload"
            accept="video/*"
            multiple
            onChange={handleFileChange}
            disabled={isUploading}
          />

          <button
            type="button"
            onClick={() => setShowLinkModal((prev) => !prev)}
            style={{
              padding: '14px 28px',
              borderRadius: '14px',
              fontWeight: 700,
              fontSize: '1rem',
              color: '#ffffff',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(147, 51, 234, 0.9) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.3s ease',
            }}
          >
            + Klistra in Videolänk (YouTube/MP4)
          </button>
        </div>
      )}

      {/* Upload status banner */}
      {uploadStatus && (
        <div
          style={{
            padding: '12px 24px',
            marginBottom: '24px',
            borderRadius: '12px',
            background: 'rgba(99, 102, 241, 0.2)',
            border: '1px solid rgba(99, 102, 241, 0.5)',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '0.95rem',
            textAlign: 'center',
          }}
        >
          {uploadStatus}
        </div>
      )}

      {/* Modal / Formulär för videolänk */}
      {showLinkModal && canEdit && (
        <form
          onSubmit={handleAddLinkSubmit}
          style={{
            width: '100%',
            maxWidth: '600px',
            background: 'rgba(30, 27, 75, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '40px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
          }}
        >
          <h3 style={{ color: '#fff', margin: 0, fontSize: '1.2rem' }}>Lägg till videolänk</h3>
          <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0, fontSize: '0.85rem' }}>
            Klistra in en YouTube-länk, Vimeo-länk eller direkt MP4-webbadress. Fungerar för alla enheter!
          </p>
          <input
            type="text"
            placeholder="Titel (t.ex. Fotbollsfinalen Reel)"
            value={linkTitle}
            onChange={(e) => setLinkTitle(e.target.value)}
            style={{
              padding: '12px 16px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#fff',
              fontSize: '0.95rem',
              outline: 'none',
            }}
          />
          <input
            type="url"
            placeholder="https://www.youtube.com/watch?v=... eller https://...video.mp4"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            required
            style={{
              padding: '12px 16px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#fff',
              fontSize: '0.95rem',
              outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '6px' }}>
            <button
              type="button"
              onClick={() => setShowLinkModal(false)}
              style={{
                padding: '10px 18px',
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={isUploading}
              style={{
                padding: '10px 22px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                border: 'none',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Spara video
            </button>
          </div>
        </form>
      )}

      {/* Videor i responsivt rutnät */}
      <div className={styles.videoPreviewContainer}>
        {videos.length === 0 ? (
          <div
            style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '60px 20px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '1.1rem',
            }}
          >
            Inga videor uppladdade ännu. Ladda upp din första video eller klistra in en videolänk ovan!
          </div>
        ) : (
          videos.map((video, index) => {
            const embedInfo = getEmbedInfo(video.url);

            return (
              <div key={video.id || video._id || `vid-${index}`} className={styles.videoWrapper}>
                {canEdit && (
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => handleRemoveVideo(video, index)}
                  >
                    Ta bort
                  </button>
                )}

                {embedInfo.isEmbed ? (
                  <iframe
                    src={embedInfo.src}
                    title={video.title || 'Video Reel'}
                    className={styles.videoPreview}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ border: 'none', width: '100%', height: '280px', display: 'block' }}
                  />
                ) : (
                  <video
                    src={video.url}
                    controls
                    preload="metadata"
                    className={styles.videoPreview}
                    style={{ width: '100%', maxHeight: '320px', objectFit: 'cover' }}
                  />
                )}

                <div style={{ padding: '16px' }}>
                  <div className={styles.videoTitle}>
                    {video.title || video.filename || 'HealthWeek Video'}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default withEditRights(VideoReels);
